import React, { useState } from 'react'
import type {
  primitives,
  AuctionBlueprintOnlineReactiveArgs,
} from '@adc/blueprint'
// @ts-ignore no types
import { get } from 'lodash'
// @ts-ignore no types
import { connect } from 'react-redux'
import { useUnidirectionalPresence } from '@adc/presence'
import { hooks, useApplicationContext } from '@adc/ui-adc'
import type { FeatureFlags } from '@adc/ui-adc'
// @ts-ignore no types
import newAnalytics from '@adc/analytics'
import analytics from '../services/analytics'
import { PresenceContext } from '../contexts'
import AuctionApp from './auctionApp'

import '@adc/ui-adc/lib/styles.css'

type Listing = AuctionBlueprintOnlineReactiveArgs['listing']

interface AppProps {
  featureFlags: FeatureFlags
  listing: Listing
  user: primitives.AuctionUser
  ssrHints?: { announcementDismissedFingerprint?: string }
}

const App = ({ featureFlags, listing, user, ssrHints }: AppProps) => {
  const { graphWebsocket } = useApplicationContext()
  // TODO: Push this down into `@adc/presence` since it's just an annoying detail at this level
  const isMounted = hooks.useIsMounted()
  const [presenceContext, updatePresenceContext] = useState({
    platform: 'web',
    source: 'pdp',
    is_registered: Boolean(
      listing.is_current_user_registered_for_online_auction
    ),
  })

  const presenceChannelKey = `auctioncom.listing.${listing.listing_id}`

  useUnidirectionalPresence(presenceChannelKey, presenceContext, {
    enabled: isMounted,
    graphWebsocket,
  })

  hooks.useMountEffect(() => {
    // NOTE: These analytics invocations are different because they were squashed together when the apps were combined
    // a few years ago and never refactored to be made consistent. Will need to work with Sheldon and confirm we don't
    // break any tracking they currently rely on when we re-work this, and, hopefully, we can track a property mostly
    // the same way regardless of product type. There are more things in common here than there are differences - we
    // should take advantage of that fact.
    if (listing.__methods__?.isTrustee()) {
      analytics.trackPDP({ listing, user, featureFlags })
    } else {
      const propertyContext = {
        'auction.id': get(listing, 'event.event_id', '0'),
        'property.type': get(
          listing,
          'primary_property.summary.structure_type_code'
        ),
        'property.equityNumber': get(listing, 'potential_return_score') || 0,
        venueId: get(listing, 'venue.venue_id', ''),
        statusLabel: get(listing, 'listing_status_label'),
        // TODO: type analytics package
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        listing: newAnalytics.standardTracking.listing(listing),
        estCreditBid: listing.reserve_price,
        reserve: get(listing, 'listing.reserve_price'),
        marketingTags: (() => {
          const tags = (get(listing, 'marketing_tags', []) || [])
            .map((tag: any) => tag?.tag)
            .filter(Boolean)
          return tags.length > 0 ? tags : null
        })(),
      }

      analytics.initialize(propertyContext, user, featureFlags)
    }
  })

  return (
    <PresenceContext.Provider
      value={{ updatePresenceContext: updatePresenceContext as () => void }}
    >
      <AuctionApp
        isSaved={Boolean(listing.primary_property.is_currently_saved)}
        ssrHints={ssrHints}
      />
    </PresenceContext.Provider>
  )
}

const mapStateToProps = (store: any) => ({
  user: store.user,
  listing: store.listing,
  featureFlags: store.featureFlags,
})

export default connect(mapStateToProps)(App)
